import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as ArrowLeft } from '@elementary/common-ui/assets/arrow-left.svg';
import { ReactComponent as ArrowRight } from '@elementary/common-ui/assets/arrow-right-2.svg';
import { ReactComponent as CloseIcon } from '@elementary/common-ui/assets/close.svg';
import {
  Button,
  Flex,
  FlexColumn,
  IconButton,
  LoadingButton,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { distinctArray } from '@elementary/common-ui/utils/common';
import { Typography, Dialog, Divider, Tooltip } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { AutomatedTestSeverities } from 'shared/constants';
import useEnvironments from 'shared/hooks/useEnvironments';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/api';
import {
  AddTestForm,
  AddTestApi,
  CLOUD_TEST_TYPES,
  ColumnResource,
  AddTestApiItem,
  Resource,
  AddTestBaseApiItem,
  AddSingularTestApi,
  ConvertQueryApi,
  AddYamlTestApiItem,
  EditGenericTestApi,
  AddCloudTestApi,
  CheckDuplicatesResponse,
  DuplicateCloudTest,
} from 'shared/types';
import { WizardMode } from 'testManagement/types';
import {
  getConditionalSeverityValue,
  getTestArgs,
  isStageValid,
} from 'testManagement/utils/utils';
import { getArrayPropertyChanges } from 'catalog/components/BulkEditWizard/utils';
import useTestsPolling from 'shared/hooks/useTestsPolling';
import useTicketField from 'shared/hooks/useTicketField';
import {
  ADD_TEST_RESOURCES_TABLE_FILTERS,
  ADD_TEST_RESOURCES_TABLE_SELECT_ALL,
} from '@elementary/common-ui/constants/urlParams';
import {
  TEST_MANAGEMENT_CLOUD_TESTS,
  PULL_REQUESTS,
  RESOURCES,
  TEST_MANAGEMENT_TESTS,
} from '@elementary/common-ui/constants/queryKeys';
import ConnectTicketDropdown from 'shared/components/ConnectTicketDropdown';
import { CreateTicketDialogs } from 'shared/components/CreateTicketDialogs';
import AddCustomQueryTestWizardStages from './AddCustomQueryTestWizardStages';
import AddTestWizardStages from './AddTestWizardStages';
import EditTestWizardStages from './EditTestWizardStages';
import TestDialogStepper from './TestDialogStepper';
import OverrideTestsConfirmationDialog from './OverrideTestsConfirmationDialog';

const Wrapper = styled(Dialog)`
  & .MuiDialog-paper {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
`;

const Header = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 24px;
`;

const Footer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  height: 80px;
  gap: 16px;
  border-top: 1px solid ${({ theme }) => theme.palette.common.lightBlue3};
  flex-shrink: 0;
`;

const FooterRight = styled(Flex)`
  align-items: center;
  gap: 16px;
  margin-left: auto;
`;

const Title = styled(Typography)`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const Form = styled.form`
  height: calc(100% - 152px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ButtonIconWrapper = styled(Flex)<{ $disabled: boolean }>`
  svg {
    path {
      fill: ${({ $disabled, theme }) =>
        $disabled ? theme.palette.common.gray11 : 'auto'};
      transition: fill 0.3s;
    }
  }
`;

const testPackageMap: Record<string, 'elementary' | 'dbt'> = {
  freshnessAnomalies: 'elementary',
  eventFreshnessAnomalies: 'elementary',
  volumeAnomalies: 'elementary',
  dimensionAnomalies: 'elementary',
  schemaChanges: 'elementary',
};

const SELECT_TICKET_TO_CONTINUE = 'Select a ticket to continue';

const tooltipMap: (
  mode: WizardMode,
  editMode?: boolean,
) => Record<number, string | undefined> = (mode, editMode) => {
  if (mode === 'customQuery') {
    return {
      1: 'Enter a query to continue',
      2: 'Complete the form to continue',
      3: SELECT_TICKET_TO_CONTINUE,
    };
  }
  if (editMode) {
    return {
      1: 'Modify the configuration to continue',
      2: SELECT_TICKET_TO_CONTINUE,
    };
  }
  return {
    1: 'Select a target to continue',
    2: 'Select a test to continue',
    3: 'Complete the form to continue',
    4: SELECT_TICKET_TO_CONTINUE,
  };
};

const testTypeMap: {
  [key in AddTestForm['testType']]: string;
} = {
  freshnessAnomalies: 'freshness_anomalies',
  cloudFreshnessAnomalies: 'freshness_anomalies',
  eventFreshnessAnomalies: 'event_freshness_anomalies',
  volumeAnomalies: 'volume_anomalies',
  cloudVolumeAnomalies: 'volume_anomalies',
  dimensionAnomalies: 'dimension_anomalies',
  schemaChanges: 'schema_changes',
  cloudDataContract: 'data_contract',
  cloudPipelineTaskPerformance: 'pipeline_task_performance',
  unique: 'unique',
  notNull: 'not_null',
  acceptedValues: 'accepted_values',
  relationship: 'relationships',
  yamlTest: '',
  customQuery: '',
  '': '',
};

type Props = {
  open: boolean;
  mode?: WizardMode;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  editMode?: boolean;
  initialValues?: Partial<AddTestForm>;
  editTestMetadata?: {
    name?: string;
    type?: string;
    subType?: string;
    resourceName?: string;
    tableName?: string;
    package?: string;
  };
};

const AddTestDialog = ({
  open,
  mode = 'tables',
  onClose,
  onSubmitSuccess,
  editMode,
  initialValues,
  editTestMetadata,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stage, setStage] = useState(1);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [keepConfigStageMounted, setKeepConfigStageMounted] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateCloudTest[]>([]);
  const [pendingCloudTestArgs, setPendingCloudTestArgs] =
    useState<AddCloudTestApi | null>(null);

  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { selectedEnv, selectedEnvId } = useEnvironments();
  const navigate = useNavigateWithEnv();
  const { pathname } = useLocation();
  const { setPollingTestsIds } = useTestsPolling();

  const warehouseType = selectedEnv?.dwhConnection?.warehouseType;

  const methods = useForm<AddTestForm>({
    shouldUnregister: true,
    mode: 'onChange',
  });

  const {
    ticketIdRef,
    requireTicketId,
    enabledIntegrations,
    activeIntegration,
    handleIntegrationSelect,
    createTicketDialogOpen,
    handleCreateTicketClick,
    handleCreateTicketClose,
    handleTicketCreated,
    resetTicket,
  } = useTicketField({
    envId: selectedEnvId,
    ticketId: methods.watch('ticketId') ?? null,
    setTicketId: (id) =>
      methods.setValue('ticketId', id, {
        shouldDirty: true,
        shouldValidate: true,
      }),
  });

  // This logic is to avoid changing the url by multiple logics simultaneously
  const navigateWithDelay = useCallback(
    (url: string) => {
      setTimeout(() => {
        navigate(url);
      }, 50);
    },
    [navigate],
  );

  const { data: resourcesData, isLoading: isResourcesLoading } = useQuery(
    [RESOURCES, selectedEnvId],
    () => api.fetchResources(selectedEnvId!),
    { enabled: !!selectedEnvId },
  );

  const { data: singularTestPaths } = useQuery(
    [TEST_MANAGEMENT_TESTS, selectedEnvId],
    () => api.fetchTestManagementTests(selectedEnvId!),
    {
      enabled: !!selectedEnvId,
      select: (data) => {
        const paths = data.tests
          .filter((test) => test.is_singular)
          .map((t) => {
            const segments = t.original_path.split('/');
            segments.pop();
            return segments.join('/');
          });
        return distinctArray(paths);
      },
    },
  );

  const clearDialogSearchParams = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete(ADD_TEST_RESOURCES_TABLE_FILTERS);
    params.delete(ADD_TEST_RESOURCES_TABLE_SELECT_ALL);

    setSearchParams(params.toString());
  }, [searchParams, setSearchParams]);

  const handleSubmitSuccess = () => {
    onClose();
    setStage(1);
    methods.setValue('resourceType', 'tables');
    methods.setValue('testType', '');
    resetTicket();
    clearDialogSearchParams();
    onSubmitSuccess?.();
  };

  const onDbtTestSubmitSuccess = () => {
    handleSubmitSuccess();
    navigateWithDelay('/pull-requests');
  };

  const { mutateAsync: addTests, isLoading: isAddTestsLoading } = useMutation(
    (value: AddTestApi) =>
      api.addTests(value, selectedEnvId!, ticketIdRef.current || undefined),
    {
      onSuccess: onDbtTestSubmitSuccess,
    },
  );

  const { mutateAsync: addSingularTest, isLoading: isAddSingularTestLoading } =
    useMutation(
      (value: AddSingularTestApi) =>
        api.addSingularTest(
          value,
          selectedEnvId!,
          ticketIdRef.current || undefined,
        ),
      {
        onSuccess: onDbtTestSubmitSuccess,
      },
    );

  const { mutateAsync: addYamlTests, isLoading: isAddYamlTestLoading } =
    useMutation(
      (value: AddTestApi) =>
        api.addYamlTests(
          value,
          selectedEnvId!,
          ticketIdRef.current || undefined,
        ),
      {
        onSuccess: onDbtTestSubmitSuccess,
      },
    );

  const { mutateAsync: checkDuplicates, isLoading: isCheckDuplicatesLoading } =
    useMutation(
      (value: AddCloudTestApi) =>
        api.checkDuplicateCloudTests(value, selectedEnvId!),
      {
        onSuccess: (
          res: CheckDuplicatesResponse,
          variables: AddCloudTestApi,
        ) => {
          if (res.duplicates.length > 0) {
            setDuplicates(res.duplicates);
            setPendingCloudTestArgs(variables);
            setShowOverrideDialog(true);
          } else {
            addCloudTest({ value: variables, override: false });
          }
        },
      },
    );

  const { mutateAsync: addCloudTest, isLoading: isAddCloudTestLoading } =
    useMutation(
      ({ value, override }: { value: AddCloudTestApi; override: boolean }) =>
        api.addCloudTest(value, selectedEnvId!, override),
      {
        onSuccess: (res) => {
          openSnackbar({
            message: `Cloud ${res?.length > 1 ? 'tests' : 'test'} ${
              duplicates.length > 0 ? 'updated' : 'added'
            } successfully! Changes will be applied shortly.`,
            variant: 'success',
          });

          queryClient.invalidateQueries({
            queryKey: [TEST_MANAGEMENT_CLOUD_TESTS, selectedEnvId],
          });

          setPollingTestsIds((prev) => {
            const newSet = new Set(prev);
            res.forEach((r: { test_id: string }) => newSet.add(r.test_id));
            return newSet;
          });

          setDuplicates([]);
          setPendingCloudTestArgs(null);
          handleSubmitSuccess();

          if (!pathname.includes('/test-management')) {
            navigateWithDelay('/test-management');
          }
        },
      },
    );

  const handleOverrideConfirm = async () => {
    setShowOverrideDialog(false);
    if (pendingCloudTestArgs) {
      await addCloudTest({ value: pendingCloudTestArgs, override: true });
    }
  };

  const handleOverrideCancel = () => {
    setShowOverrideDialog(false);
    setDuplicates([]);
    setPendingCloudTestArgs(null);
  };

  const {
    mutate: convertQuery,
    isLoading: isConvertQueryLoading,
    error: convertQueryError,
  } = useMutation(
    (value: ConvertQueryApi) => api.convertQuery(value, selectedEnvId!),
    {
      onSuccess: (res) => {
        methods.setValue('customQueryConverted', res.converted_sql_query);
        setStage((prev) => prev + 1);
      },
    },
  );

  const { mutateAsync: editGenericTest, isLoading: isEditGenericTestLoading } =
    useMutation(
      (args: { data: { edit_tests: EditGenericTestApi[] } }) =>
        api.editGenericTest(
          args,
          selectedEnvId!,
          ticketIdRef.current || undefined,
        ),
      {
        onSuccess: handleSubmitSuccess,
      },
    );

  const tableResourcesData = useMemo(
    () => resourcesData ?? [],
    [resourcesData],
  );

  const columnsResourcesData: ColumnResource[] = useMemo(
    () =>
      tableResourcesData
        .filter((item) => item.source_type !== 'python_sdk')
        .map((item) =>
          item.columns.map((col) => ({
            id: `${item.id}.${col.name}`,
            asset_id: item.id,
            schema: item.schema,
            model_id: item.id,
            model_name: item.name,
            source_name: item.source_name,
            owners: item.owners,
            tags: item.tags,
            table_type: item.table_type,
            source_type: item.source_type,
            table_fqn: item.fqn,
            db_name: item.db_name,
            table_name: item.table_name || item.name,
            ...col,
          })),
        )
        .flat(),
    [tableResourcesData],
  );

  const getCommonSubmitArgs = (data: AddTestForm): AddTestBaseApiItem => ({
    name:
      data.testType === 'customQuery'
        ? data.testName
        : testTypeMap[data.testType],
    owners: data.owners,
    description: data.description !== '' ? data.description : undefined,
    tags: data.tags,
    severity: data.severity?.type,
    quality_dimension: data.qualityDimension,
  });

  const isValueEdited = (
    key: keyof AddTestForm,
    value?: AddTestForm[keyof AddTestForm],
  ) =>
    JSON.stringify(initialValues?.[key] || {}) !== JSON.stringify(value || {});

  const isTestConfigEdited =
    !!initialValues &&
    Object.keys(initialValues).some((key) =>
      isValueEdited(
        key as keyof AddTestForm,
        methods.watch(key as keyof AddTestForm),
      ),
    );

  const onSubmit = async (data: AddTestForm) => {
    const resourceType = methods.watch('resourceType');
    const isCustomQuery = methods.watch('testType') === 'customQuery';
    const isYamlTest = methods.watch('testType') === 'yamlTest';
    const isCloudTest = (CLOUD_TEST_TYPES as readonly string[]).includes(
      methods.watch('testType'),
    );

    const sourceName = tableResourcesData.find(
      (r) => r.id === methods.watch('resources')?.[0],
    )?.source_name;

    if (editMode) {
      const originalOwners = initialValues?.owners || [];
      const originalTags = initialValues?.tags || [];
      const currentOwners = data.owners || [];
      const currentTags = data.tags || [];
      const ownersChanges = isValueEdited('owners', data.owners)
        ? getArrayPropertyChanges(originalOwners, currentOwners)
        : undefined;
      const tagsChanges = isValueEdited('tags', data.tags)
        ? getArrayPropertyChanges(originalTags, currentTags)
        : undefined;

      const editArgs: EditGenericTestApi[] = data.resources.map((resource) => {
        const resourceDataItem =
          methods.watch('resourceType') === 'columns'
            ? columnsResourcesData.find((res) => res.id === resource)
            : tableResourcesData.find((res) => res.id === resource);
        const assetId =
          methods.watch('resourceType') === 'columns' && resourceDataItem
            ? (resourceDataItem as ColumnResource).asset_id
            : resource;

        return {
          owners: ownersChanges,
          tags: tagsChanges,
          description: isValueEdited('description', data.description)
            ? data.description
            : undefined,
          severity: isValueEdited('severity', data.severity)
            ? data.severity?.type
            : undefined,
          ...(isValueEdited('severity', data.severity)
            ? getConditionalSeverityValue(methods.watch)
            : {}),
          name: editTestMetadata?.name || '',
          resource_name: editTestMetadata?.tableName || '',
          source_name: sourceName,
          package: editTestMetadata?.package,
          asset_id: assetId,
          column:
            methods.watch('resourceType') === 'columns'
              ? editTestMetadata?.resourceName?.split('.').pop()?.toLowerCase()
              : undefined,
        };
      });

      await editGenericTest({ data: { edit_tests: editArgs } });
    } else if (isCustomQuery) {
      const arg: AddSingularTestApi = {
        ...getCommonSubmitArgs(data),
        path: data.path,
        sql_query: data.customQueryConverted,
        override_primary_test_model_id: data.testedTable,
      };

      await addSingularTest(arg);
    } else if (isCloudTest) {
      const resourceData: Resource[] = tableResourcesData; // For the future we will support column cloud tests

      const isDataContractCloudTest = data.testType === 'cloudDataContract';

      const addCloudTestArgs: AddCloudTestApi = data.resources.map(
        (resource) => {
          const dataItem = resourceData.find((res) => res.id === resource);

          const baseArgs = {
            test_type: testTypeMap[data.testType] || '',
            database_name: dataItem?.db_name || null,
            schema_name: dataItem?.schema || '',
            table_name: dataItem?.table_name || dataItem?.name || '',
            model_unique_id: dataItem?.id || '',
            owners: data.owners,
            tags: data.tags,
            severity:
              data.severity?.type === 'error'
                ? AutomatedTestSeverities.ERROR
                : AutomatedTestSeverities.WARN,
          };

          if (isDataContractCloudTest) {
            return {
              ...baseArgs,
              baseline_mode: data.baselineMode || 'complete',
              ...(data.baselineMode === 'manual'
                ? { baseline_columns: data.baselineColumns }
                : {}),
              enforcement_level: data.enforcementLevel || 'all',
            };
          }

          const {
            sensitivity,
            anomalyDirection,
            detectionPeriod,
            fixedThreshold,
            trainingPeriod,
            spikeThreshold,
            dropThreshold,
          } = data?.anomalyConfig || {};

          if (data.testType === 'cloudPipelineTaskPerformance') {
            const isPipelineThreshold = fixedThreshold != null;
            return {
              ...baseArgs,
              config: isPipelineThreshold
                ? {
                    mode: 'threshold',
                    fixed_threshold: fixedThreshold,
                  }
                : {
                    mode: 'anomaly',
                    ...(sensitivity ? { sensitivity } : {}),
                    ...(anomalyDirection
                      ? { anomaly_direction: anomalyDirection }
                      : {}),
                    ...(trainingPeriod
                      ? { training_period: trainingPeriod }
                      : {}),
                  },
            };
          }

          const isVolumeThresholdMode =
            data.testType === 'cloudVolumeAnomalies' &&
            data.volumeDetectionMode === 'percentage_thresholds';

          const includeSpike =
            isVolumeThresholdMode &&
            spikeThreshold &&
            anomalyDirection !== 'DROP';
          const includeDrop =
            isVolumeThresholdMode &&
            dropThreshold &&
            anomalyDirection !== 'SPIKE';

          return {
            ...baseArgs,
            anomaly_config: {
              // In threshold mode the runner ignores sensitivity, so don't send it.
              ...(sensitivity && !isVolumeThresholdMode ? { sensitivity } : {}),
              ...(anomalyDirection
                ? { anomaly_direction: anomalyDirection }
                : {}),
              ...(detectionPeriod ? { detection_period: detectionPeriod } : {}),
              ...(fixedThreshold ? { fixed_threshold: fixedThreshold } : {}),
              ...(trainingPeriod ? { training_period: trainingPeriod } : {}),
              ...(includeSpike ? { spike_threshold: spikeThreshold } : {}),
              ...(includeDrop ? { drop_threshold: dropThreshold } : {}),
            },
          };
        },
      );

      await checkDuplicates(addCloudTestArgs);
    } else {
      const resourceData: (Resource | ColumnResource)[] =
        resourceType === 'tables' ? tableResourcesData : columnsResourcesData;

      const addTestArg: AddTestApi = data.resources.map((resource) => {
        const dataItem = resourceData.find((res) => res.id === resource);

        const resourceName =
          resourceType === 'tables'
            ? dataItem?.name
            : (dataItem as ColumnResource)?.model_name;

        const argBase = {
          ...getCommonSubmitArgs(data),
          resource_name: resourceName || '',
          source_name: resourceData.find((res) => res.id === resource)
            ?.source_name,
        };

        let arg: AddYamlTestApiItem | AddTestApiItem;

        const assetId =
          resourceType === 'columns' && dataItem
            ? (dataItem as ColumnResource).asset_id
            : resource;

        if (isYamlTest) {
          arg = {
            ...argBase,
            ...getConditionalSeverityValue(methods.watch),
            yaml: data.yaml,
            name: 'yamlTest',
            asset_id: assetId,
            where: data.whereClause || undefined,
            limit: data.limit || undefined,
            store_failures: data.shouldStoreFailures,
          };
        } else {
          arg = {
            ...argBase,
            package: testPackageMap[data.testType],
            ...getConditionalSeverityValue(methods.watch),
            args: getTestArgs(data),
            asset_id: assetId,
          };
        }

        if (resourceType === 'columns') {
          arg.column = resourceData.find((res) => res.id === resource)?.name;
        }
        return arg;
      });

      const submissionMethod = isYamlTest ? addYamlTests : addTests;
      await submissionMethod(addTestArg);
    }

    queryClient.invalidateQueries([PULL_REQUESTS]);
  };

  const handleClose = () => {
    setShowCloseConfirmDialog(false);
    onClose();
    setStage(1);
    resetTicket();

    methods.setValue('resourceType', 'tables');
    methods.setValue('testType', '');
    methods.setValue('yamlTemplateName', '');

    clearDialogSearchParams();
  };

  const resourceTypeWatch = methods.watch('resourceType');
  const testTypeWatch = methods.watch('testType');

  useEffect(() => {
    setKeepConfigStageMounted(false);
  }, [testTypeWatch]);

  useEffect(() => {
    if (open) {
      if (editMode) {
        methods.setValue('testType', 'yamlTest');
        Object.entries(initialValues || {}).forEach(([key, value]) => {
          methods.setValue(key as keyof AddTestForm, value);
        });
      } else if (mode === 'customQuery') {
        methods.setValue('testType', mode);
      } else {
        methods.setValue('resourceType', mode);
      }
    } else {
      methods.setValue('testType', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, resourceTypeWatch, editMode, open]);

  const isLastStage = editMode
    ? stage === 2
    : mode === 'customQuery'
    ? stage === 3
    : stage === 4;

  const isTicketRequired =
    requireTicketId &&
    !!activeIntegration &&
    !(CLOUD_TEST_TYPES as readonly string[]).includes(testTypeWatch);

  const injectNameIntoYaml = useCallback(() => {
    const testNameOverride = methods.watch('testNameOverride');
    const currentYaml = methods.watch('yaml');

    if (!testNameOverride?.trim() || !currentYaml) return;

    // Parse the YAML, add the name property, and re-serialize
    try {
      const lines = currentYaml.split('\n');
      // Find the test name line (first non-comment, non-empty line with a key)
      const testLineIndex = lines.findIndex(
        (line) =>
          line.trim() && !line.trim().startsWith('#') && line.includes(':'),
      );

      if (testLineIndex === -1) return;

      // Find the indentation level of the test properties
      const nextContentLine = lines
        .slice(testLineIndex + 1)
        .find((line) => line.trim() && !line.trim().startsWith('#'));
      const indent = nextContentLine
        ? nextContentLine.match(/^(\s*)/)?.[1] || '  '
        : '  ';

      // Check if name already exists in the yaml
      const nameLineIndex = lines.findIndex(
        (line) =>
          line.trim().startsWith('name:') && !line.trim().startsWith('#'),
      );

      if (nameLineIndex !== -1) {
        // Update existing name line
        lines[nameLineIndex] = `${indent}name: '${testNameOverride.trim()}'`;
      } else {
        // Insert name as the last property before any trailing comments or empty lines
        // Find the last non-empty, non-comment line
        let insertIndex = lines.length;
        for (let i = lines.length - 1; i > testLineIndex; i--) {
          const line = lines[i].trim();
          if (line && !line.startsWith('#')) {
            insertIndex = i + 1;
            break;
          }
        }
        lines.splice(
          insertIndex,
          0,
          `${indent}name: '${testNameOverride.trim()}'`,
        );
      }

      methods.setValue('yaml', lines.join('\n'));
    } catch {
      // If parsing fails, don't modify the yaml
    }
  }, [methods]);

  const handleNextClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (!isLastStage) {
      e.preventDefault();
      if (mode === 'customQuery' && stage === 1) {
        setKeepConfigStageMounted(true);
        convertQuery({
          sql_query: methods.watch('customQuery'),
          code_type: 'sql',
        });
      } else {
        setKeepConfigStageMounted(true);
        // When transitioning from stage 3 (configuration) to stage 4 (review) for yaml tests
        if (stage === 3 && methods.watch('testType') === 'yamlTest') {
          injectNameIntoYaml();
        }
        setStage((prev) => prev + 1);
      }
    }
  };

  return (
    <Wrapper open={open} maxWidth={false}>
      <Header>
        <Title>{editMode ? 'Edit test' : 'Add test'}</Title>
        <IconButton
          onClick={() =>
            methods.watch('resources')?.length || !!methods.watch('customQuery')
              ? setShowCloseConfirmDialog(true)
              : handleClose()
          }
        >
          <CloseIcon />
        </IconButton>
      </Header>

      <TestDialogStepper
        mode={mode}
        stage={stage}
        watch={methods.watch}
        onStageClick={(index) => setStage(index)}
        tableResourcesData={tableResourcesData}
        columnsResourcesData={columnsResourcesData}
        editMode={editMode}
      />

      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        {editMode && !!initialValues ? (
          <EditTestWizardStages
            methods={methods}
            stage={stage}
            columnsResourcesData={columnsResourcesData}
            testType={editTestMetadata?.type}
            testSubType={editTestMetadata?.subType}
            testName={editTestMetadata?.name}
            resourceName={editTestMetadata?.resourceName}
            originalFormValues={initialValues}
          />
        ) : mode === 'customQuery' ? (
          <AddCustomQueryTestWizardStages
            methods={methods}
            stage={stage}
            keepConfigStageMounted={keepConfigStageMounted}
            singularTestPaths={singularTestPaths}
            tableResourcesData={tableResourcesData}
          />
        ) : (
          <AddTestWizardStages
            methods={methods}
            stage={stage}
            tableResourcesData={tableResourcesData}
            columnsResourcesData={columnsResourcesData}
            keepConfigStageMounted={keepConfigStageMounted}
            isResourcesLoading={isResourcesLoading}
            warehouseType={warehouseType}
          />
        )}

        <Footer>
          {stage > 1 ? (
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setStage((prev) => prev - 1)}
              startIcon={
                <ButtonIconWrapper $disabled={stage === 1}>
                  <ArrowLeft />
                </ButtonIconWrapper>
              }
            >
              Back
            </Button>
          ) : (
            <div />
          )}

          <FooterRight>
            {activeIntegration &&
              isLastStage &&
              !(CLOUD_TEST_TYPES as readonly string[]).includes(
                testTypeWatch,
              ) && (
                <>
                  <Controller
                    name="ticketId"
                    control={methods.control}
                    shouldUnregister={false}
                    render={({ field }) => (
                      <ConnectTicketDropdown
                        envId={selectedEnvId!}
                        enabledIntegrations={enabledIntegrations}
                        onSelect={(integration, ticket) => {
                          handleIntegrationSelect(integration.id);
                          field.onChange(ticket.key);
                        }}
                        onCreateClick={(integration) =>
                          handleCreateTicketClick(integration.id)
                        }
                        selectedTicket={
                          field.value
                            ? {
                                key: field.value,
                                integrationType: activeIntegration.type,
                              }
                            : null
                        }
                        onClearSelected={() => field.onChange(null)}
                        popoverPlacement="above"
                      />
                    )}
                  />
                  <CreateTicketDialogs
                    open={createTicketDialogOpen}
                    onClose={handleCreateTicketClose}
                    onTicketCreated={handleTicketCreated}
                    activeIntegration={activeIntegration}
                    envId={selectedEnvId!}
                  />
                </>
              )}

            <Tooltip
              title={
                !isStageValid(
                  methods,
                  stage,
                  editMode,
                  isTestConfigEdited,
                  isTicketRequired,
                )
                  ? tooltipMap(mode, editMode)[stage] ?? ''
                  : ''
              }
            >
              <Flex sx={{ alignItems: 'center', gap: 2 }}>
                {!!convertQueryError && (
                  <OverflowTooltip
                    sx={{ maxWidth: '350px' }}
                    typographySx={{ color: 'common.red', fontSize: '11px' }}
                  >
                    {`Error: ${
                      (convertQueryError as any).response.data.extra.error ||
                      'Error processing query'
                    }`}
                  </OverflowTooltip>
                )}
                <LoadingButton
                  size="medium"
                  disabled={
                    !isStageValid(
                      methods,
                      stage,
                      editMode,
                      isTestConfigEdited,
                      isTicketRequired,
                    )
                  }
                  type={isLastStage ? 'submit' : 'button'}
                  loading={
                    isAddTestsLoading ||
                    isAddSingularTestLoading ||
                    isAddYamlTestLoading ||
                    isConvertQueryLoading ||
                    isEditGenericTestLoading ||
                    isAddCloudTestLoading ||
                    isCheckDuplicatesLoading
                  }
                  onClick={handleNextClick}
                  endIcon={
                    <ButtonIconWrapper
                      $disabled={
                        !isStageValid(
                          methods,
                          stage,
                          editMode,
                          isTestConfigEdited,
                          isTicketRequired,
                        )
                      }
                    >
                      <ArrowRight />
                    </ButtonIconWrapper>
                  }
                >
                  {isLastStage
                    ? (CLOUD_TEST_TYPES as readonly string[]).includes(
                        testTypeWatch,
                      )
                      ? 'Submit'
                      : 'Submit & Open PR'
                    : 'Next'}
                </LoadingButton>
              </Flex>
            </Tooltip>
          </FooterRight>
        </Footer>
      </Form>

      <Dialog open={showCloseConfirmDialog}>
        <FlexColumn sx={{ width: '416px' }}>
          <FlexColumn sx={{ gap: 2, p: 3 }}>
            <Typography sx={{ fontWeight: 'bold' }}>Unsaved changes</Typography>
            <Typography>Your changes will be lost if you exit.</Typography>
          </FlexColumn>
          <Divider />
          <Flex sx={{ gap: 1, p: 3, justifyContent: 'flex-end' }}>
            <Button
              color="secondary"
              size="large"
              onClick={() => setShowCloseConfirmDialog(false)}
            >
              Keep editing
            </Button>
            <Button size="large" onClick={handleClose}>
              Discard changes
            </Button>
          </Flex>
        </FlexColumn>
      </Dialog>

      <OverrideTestsConfirmationDialog
        open={showOverrideDialog}
        onClose={handleOverrideCancel}
        onConfirm={handleOverrideConfirm}
        duplicates={duplicates}
        isLoading={isAddCloudTestLoading}
        totalSubmittedTestsCount={pendingCloudTestArgs?.length || 0}
      />
    </Wrapper>
  );
};

export default AddTestDialog;
